import React, { useState } from "react";
import { Box, IconButton, Tooltip, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVertRounded";

// Project import
import TextInformation from "./textInformation";
import { MenuItemProps } from "./customListRow";
import HoldMenuItem from "components/buttons/holdMenuItem";

interface ListItemProps {
  label?: string;
  data?: string;
  color?: string;
  icon?: React.ReactNode;
  thumbnailPath?: string;
  action?: string;
  menuItems?: MenuItemProps[];
  thumbNailOnClick?: (event: any) => void;
  menuItemsMoreMenuClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const CustomListItem: React.FC<ListItemProps> = ({
  label,
  data,
  color,
  icon,
  thumbnailPath,
  action,
  menuItems,
  thumbNailOnClick,
  menuItemsMoreMenuClick,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    menuItemsMoreMenuClick && menuItemsMoreMenuClick(event);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event: any) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  // Row menu item
  if (action === "row-menu" || data === undefined) {
    return (
      <Box key={label} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Tooltip title={label}>
          <IconButton aria-label="row-menu-item" onClick={handleClick} sx={{ mt: "2px" }}>
            <MoreVertIcon fontSize="small" color="primary" />
          </IconButton>
        </Tooltip>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
          {menuItems?.map((menuItem, index) => {
            if (menuItem && menuItem.type === "hold") {
              return (
                <HoldMenuItem
                  key={menuItem.label + "-" + index}
                  label={menuItem.label}
                  holdTime={1500}
                  onHoldComplete={() => menuItem.onClick()}
                  disabled={menuItem.disabled}
                />
              );
            }
            return (
              <MenuItem
                disabled={menuItem.disabled}
                key={index}
                onClick={(event) => {
                  event.stopPropagation(); // Stops the click event from bubbling up
                  handleClose(event);
                  menuItem.onClick();
                }}
              >
                {menuItem.label}
              </MenuItem>
            );
          })}
        </Menu>
      </Box>
    );
  }

  // Item which feature a thumbnail
  if ((thumbnailPath || icon) && label) {
    return (
      <Box key={label} sx={{ display: "flex", alignItems: "flex-start", mr: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            onClick={thumbNailOnClick}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minWidth: 44,
              maxWidth: 44,
              height: 44,
              bgcolor: "secondary.lighter",
              mr: 1,
              borderRadius: 2,
              cursor: thumbNailOnClick ? "pointer" : "default",
            }}
          >
            {thumbnailPath && (
              <img
                src={thumbnailPath}
                alt={label}
                style={{ width: "100%", height: "100%", borderRadius: 8, objectFit: "cover" }}
              />
            )}

            {icon && icon}
          </Box>
          <TextInformation label={label} data={data} />
        </Box>
      </Box>
    );
  }

  // Item which feature a label with info
  if (!color && !action && label) {
    return (
      <Box key={label} sx={{ display: "flex", mr: 1 }}>
        <TextInformation label={label} data={data} />
      </Box>
    );
  }

  // Item which feature a label with info and a colored vertical bar
  if (label) {
    return (
      <Box key={label} sx={{ display: "flex", alignItems: "center", mr: 1 }}>
        <Box
          sx={{ minWidth: 8, maxWidth: 8, height: 44, bgcolor: color, borderRadius: 2, mr: 1 }}
        />
        <TextInformation label={label} data={data} />
      </Box>
    );
  }

  return null;
};

export default CustomListItem;
